/**
 * @fileoverview OpsEmailIngestPanel — Operations → Email Intake subtab.
 *
 * v3.0 Phase 3.5 (2026-08-29) — proactive-add authorization model.
 *
 * Under the new model, senders are proactively added by an org admin
 * or editor in Org Settings. Each row lives in `email_ingest_senders`
 * with a `verification_status` of either `pending` (mailed the verify
 * link, waiting on the click) or `verified` (live binding — inbound
 * mail from this address routes to the OCR queue). The reactive
 * pending-confirmation table is gone.
 *
 * This admin surface is now a single flat list of all bindings across
 * tenants. Each row shows email, org, business, status, added_at, and
 * added_by_display_name. Revoke is the only mutation — reject-pending
 * no longer exists (a pending row that never gets verified simply
 * expires + can be removed by the org admin themselves in Org Settings).
 */

import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Format an ISO 8601 timestamp for display. Falls back to the raw string
 * if parsing fails, and returns the em-dash placeholder for empty values
 * so the table cells never look "broken" when a nullable field is null.
 *
 * @param {string|null|undefined} iso - ISO 8601 timestamp from the backend.
 * @returns {string} Locale-formatted string, the raw input, or an em-dash.
 */
function fmtTimestamp(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
}

/**
 * Render a status pill for a sender row's `verification_status`.
 * Green for verified, amber for pending, red for expired.
 */
function StatusPill({ status }) {
  const label = status || 'unknown';
  const tone = status === 'verified'
    ? 'badge--success'
    : status === 'pending'
      ? 'badge--warning'
      : 'badge--error';
  return (
    <span className={`badge ${tone}`} data-testid={`ops-email-ingest-status-${label}`}>
      {label}
    </span>
  );
}

/**
 * OpsEmailIngestPanel — Operations → Email Intake super-admin surface.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Full admin API prefix
 *   (`/api/{secret}/admin`) — pulled from AuthContext by the parent shell.
 * @returns {JSX.Element}
 */
function OpsEmailIngestPanel({ adminApiPrefix }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  /**
   * Fetch verified + pending sender rows. Empty results are a valid
   * state (nothing has been added yet) — surface them as a designed
   * empty-state, not an error.
   */
  const fetchRows = useCallback(async () => {
    if (!adminApiPrefix) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.adminEmailIngestListVerified(adminApiPrefix);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      setError(
        err?.response?.data?.error || 'Failed to load email intake bindings',
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  /**
   * Revoke a binding after a confirm prompt. On success refetches so
   * the panel reflects the new state.
   */
  async function handleRevoke(row) {
    if (!row || row.id == null) return;
    const label = row.business_name
      ? `${row.email} → ${row.business_name}`
      : row.email;
    if (!window.confirm(
      `Revoke authorization for ${label}? The mailbox will need to be `
      + 're-added and re-verified before it can send invoices again.',
    )) {
      return;
    }
    setActingId(row.id);
    setActionResult(null);
    try {
      await api.adminEmailIngestRevokeVerified(adminApiPrefix, row.id);
      setActionResult({
        ok: true,
        text: `Revoked ${label}.`,
      });
      await fetchRows();
    } catch (err) {
      const errorMsg = err?.response?.data?.error;
      setActionResult({
        ok: false,
        text: errorMsg === 'not_found'
          ? 'Row already revoked — refreshing.'
          : `Failed to revoke ${label}.`,
      });
      await fetchRows();
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="ops-panel" data-testid="ops-email-ingest-panel">
      <h2>Email Intake</h2>
      <p className="ops-panel__blurb">
        Authorized invoice email senders across every tenant. Each row
        was proactively added by an org admin or editor in their Org
        Settings and (once verified) routes inbound mail from that
        address into the OCR queue. Revoke a row to force it to be
        re-added and re-verified.
      </p>

      <div className="ops-panel__toolbar">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={fetchRows}
          disabled={loading}
          aria-label="Refresh authorized sender list"
          data-testid="ops-email-ingest-refresh-btn"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {actionResult && (
        <div
          role="status"
          aria-live="polite"
          className={`ops-panel__result ops-panel__result--${actionResult.ok ? 'ok' : 'err'}`}
          data-testid="ops-email-ingest-action-result"
        >
          {actionResult.text}
        </div>
      )}

      {error && (
        <p className="err-msg" role="alert">{error}</p>
      )}
      {loading && (
        <div className="ops-panel__loading" role="status" aria-live="polite">
          Loading authorized senders…
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="empty-state">
          <div className="empty-state__title">No authorized senders</div>
          <p className="empty-state__description">
            No mailboxes are currently authorized. Org admins add and
            verify senders in Org Settings → Authorized Invoice Email
            Addresses.
          </p>
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="tbl-wrap">
          <table aria-label="Authorized email senders">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Organization</th>
                <th scope="col">Business</th>
                <th scope="col">Status</th>
                <th scope="col">Added</th>
                <th scope="col">Added by</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`sender-${row.id}`}
                  data-testid={`ops-email-ingest-row-${row.id}`}
                >
                  <td>{row.email}</td>
                  <td>
                    {row.org_name || (
                      <span className="admin-cell-muted">
                        {row.org_id != null ? `id ${row.org_id}` : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    {row.business_name || (
                      <span className="admin-cell-muted">
                        Org-wide (pick per invoice)
                      </span>
                    )}
                  </td>
                  <td><StatusPill status={row.verification_status} /></td>
                  <td>
                    <span className="td-amt">{fmtTimestamp(row.added_at)}</span>
                  </td>
                  <td>
                    {row.added_by_display_name || (
                      <span className="admin-cell-muted">—</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRevoke(row)}
                      disabled={actingId === row.id}
                      aria-label={`Revoke authorization for ${row.email}`}
                      data-testid={`ops-email-ingest-revoke-btn-${row.id}`}
                    >
                      {actingId === row.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OpsEmailIngestPanel;
